import { apiClient } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type { ApiResponse } from "@/api/types";
import type {
  ApprovalDto, AttendanceRecordDto, CaptureResult, DayView, ExemptionDto, MeView, MonthView, ShiftAssignmentDto, ShiftAssignmentRow, ShiftTemplateDto,
} from "./hr.types";

const E = endpoints.hr;
const clean = (o: object) => Object.fromEntries(Object.entries(o as Record<string, unknown>).filter(([, v]) => v !== undefined && v !== ""));

export const attendanceService = {
  async me(): Promise<MeView> {
    const { data } = await apiClient.get<ApiResponse<MeView>>(E.attendanceMe);
    return data.data;
  },
  async checkIn(pos: { lat?: number; lng?: number; locationError?: string }): Promise<CaptureResult> {
    const { data } = await apiClient.post<ApiResponse<CaptureResult>>(E.attendanceCheckIn, clean(pos));
    return data.data;
  },
  /** Rejects with status 409 when the end-of-shift gate is closed (BA §6) — the banner opens the reconcile dialog on that. */
  async checkOut(pos: { lat?: number; lng?: number }): Promise<{ record: AttendanceRecordDto; reconciled: boolean }> {
    const { data } = await apiClient.post<ApiResponse<{ record: AttendanceRecordDto; reconciled: boolean }>>(E.attendanceCheckOut, clean(pos));
    return data.data;
  },
  async day(date?: string): Promise<DayView> {
    const { data } = await apiClient.get<ApiResponse<DayView>>(E.attendanceDay, { params: clean({ date }) });
    return data.data;
  },
  async month(month?: string): Promise<MonthView> {
    const { data } = await apiClient.get<ApiResponse<MonthView>>(E.attendanceMonth, { params: clean({ month }) });
    return data.data;
  },
  async manual(input: { employeeId: string; workDate: string; status: string; checkIn?: string | null; checkOut?: string | null; note: string }): Promise<AttendanceRecordDto> {
    const { data } = await apiClient.post<ApiResponse<AttendanceRecordDto>>(E.attendanceManual, clean(input));
    return data.data;
  },
  async exemptions(): Promise<ExemptionDto[]> {
    const { data } = await apiClient.get<ApiResponse<ExemptionDto[]>>(E.attendanceExemptions);
    return data.data;
  },
  async addExemption(input: { employeeId?: string; roleLovValueId?: string; reason?: string }) {
    const { data } = await apiClient.post<ApiResponse<{ geofence_exemption_id: string }>>(E.attendanceExemptions, clean(input));
    return data.data;
  },
  async removeExemption(id: string) {
    const { data } = await apiClient.delete<ApiResponse<{ ok: boolean }>>(`${E.attendanceExemptions}/${id}`);
    return data.data;
  },
  async events(employeeId: string) {
    const { data } = await apiClient.get<ApiResponse<Array<{ attendance_event_id: string; kind: string; occurred_at: string; capture_source: string | null; geofence_result: string; distance_m: string | null; device_ref: string | null }>>>(E.attendanceEvents(employeeId));
    return data.data;
  },
};

export const shiftsService = {
  async templates(): Promise<ShiftTemplateDto[]> {
    const { data } = await apiClient.get<ApiResponse<ShiftTemplateDto[]>>(E.shiftTemplates);
    return data.data;
  },
  async createTemplate(input: Record<string, unknown>): Promise<ShiftTemplateDto> {
    const { data } = await apiClient.post<ApiResponse<ShiftTemplateDto>>(E.shiftTemplates, clean(input));
    return data.data;
  },
  async updateTemplate(id: string, input: Record<string, unknown>): Promise<ShiftTemplateDto> {
    const { data } = await apiClient.post<ApiResponse<ShiftTemplateDto>>(E.shiftTemplate(id), input);
    return data.data;
  },
  async assignments(): Promise<ShiftAssignmentRow[]> {
    const { data } = await apiClient.get<ApiResponse<ShiftAssignmentRow[]>>(E.shiftAssignments);
    return data.data;
  },
  async assign(input: { employeeId: string; shiftTemplateId: string; effectiveFrom: string; note?: string }): Promise<ShiftAssignmentDto> {
    const { data } = await apiClient.post<ApiResponse<ShiftAssignmentDto>>(E.shiftAssignments, clean(input));
    return data.data;
  },
};

export const approvalsService = {
  async list(params: { type?: string; status?: string; employeeId?: string } = {}): Promise<{ items: ApprovalDto[]; pending: number; canDecide: boolean }> {
    const { data } = await apiClient.get<ApiResponse<{ items: ApprovalDto[]; pending: number; canDecide: boolean }>>(E.approvals, { params: clean(params) });
    return data.data;
  },
  async raise(input: { type: "LOCATION_EXCEPTION" | "WFH"; fromDate: string; toDate?: string; place?: string; reason: string; employeeId?: string; approveNow?: boolean }): Promise<ApprovalDto> {
    const { data } = await apiClient.post<ApiResponse<ApprovalDto>>(E.approvals, clean(input));
    return data.data;
  },
  async decide(id: string, decision: "approve" | "reject", note?: string): Promise<ApprovalDto> {
    const { data } = await apiClient.post<ApiResponse<ApprovalDto>>(E.approvalDecide(id, decision), clean({ note }));
    return data.data;
  },
  async cancel(id: string): Promise<ApprovalDto> {
    const { data } = await apiClient.post<ApiResponse<ApprovalDto>>(E.approvalDecide(id, "cancel"), {});
    return data.data;
  },
};

/** HH:MM from a Prisma time column's ISO instant (1970-01-01THH:MM:SSZ). */
export function timeHhmm(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = iso.match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

/** A clock time in the org's zone, e.g. "10:04 AM". */
export function clockIst(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", hour12: true }).format(new Date(iso));
}

const DAY = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export function workingDaysLabel(days: number[]): string {
  const s = [...days].sort((a, b) => a - b);
  if (s.length === 0) return "—";
  const contiguous = s.every((d, i) => i === 0 || d === s[i - 1]! + 1);
  return contiguous && s.length > 2 ? `${DAY[s[0]!]} – ${DAY[s[s.length - 1]!]}` : s.map((d) => DAY[d]).join(", ");
}

/** Ask the browser once; resolve with what it gave, never reject — the server decides what a missing position means. */
export function browserPosition(): Promise<{ lat?: number; lng?: number; locationError?: string }> {
  return new Promise((resolve) => {
    if (!("geolocation" in navigator)) return resolve({ locationError: "geolocation unsupported" });
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (err) => resolve({ locationError: err.message || `code ${err.code}` }),
      { enableHighAccuracy: true, timeout: 12_000, maximumAge: 60_000 },
    );
  });
}

/**
 * What the browser will do if asked for location: "granted" answers at once,
 * "prompt" shows the permission question, "denied" refuses without asking.
 * Browsers that cannot say report "unknown".
 *
 * Why it matters (2026-10-01): asking on page load, without a click, lets
 * Chrome show only a quiet icon in the address bar; dismissed a few times it
 * blocks the site for days and every later call fails as "User denied
 * Geolocation" — which is what locked staff out on the day the fence went on.
 */
export async function locationPermission(): Promise<"granted" | "prompt" | "denied" | "unknown"> {
  try {
    const s = await navigator.permissions?.query({ name: "geolocation" as PermissionName });
    return (s?.state as "granted" | "prompt" | "denied" | undefined) ?? "unknown";
  } catch {
    return "unknown";
  }
}
